import { useState, useEffect } from 'react';
import { TagIcon, ChevronDown, Check, ChevronRight } from 'lucide-react';
import { PopoverClose } from '@radix-ui/react-popover';
import { Model, Tag } from 'src/shared/types/model.types';
import InputWithLabel from 'src/shared/components/ui/inputs/input-with-label';
import { BaseModal, BaseModalContent } from 'src/shared/components/ui/modals/BaseModal';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useMutateTag } from 'src/features/models/queries/useMutateTag';
import { Command, CommandGroup, CommandInput, CommandItem } from 'src/shared/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'src/shared/components/ui/popover';
import { cn } from 'src/shared/lib/utils';
import TagHierarchy from 'src/shared/components/typography/TagHierarchy';
import { TextArea } from 'src/shared/components/ui/inputs/textarea';
import { Checkbox } from 'src/shared/components/ui/inputs/checkbox';
import { Label } from 'src/shared/components/typography/Label';
import { AccordionTrigger } from 'src/shared/components/ui/accordion';
import { AccordionContent, AccordionItem } from 'src/shared/components/ui/accordion';
import { Accordion } from 'src/shared/components/ui/accordion';
import { cleanSpecialCharacters } from 'src/shared/lib/textFormat.utils';

type TagCrudDialogProps = {
  model: Model;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTagName?: string;
  disableCreateMultiple?: boolean;
};

export default function TagCrudDialog({
  model,
  open,
  onOpenChange,
  initialTagName = '',
  disableCreateMultiple = false,
}: TagCrudDialogProps) {
  const { errorToast } = useToast();
  const { newTag } = useMutateTag(model.id);
  const [createAnother, setCreateAnother] = useState(false);
  const [parentId, setParentId] = useState<string | undefined>(undefined);
  const [parentSelectOpen, setParentSelectOpen] = useState(false);
  const [shortName, setShortName] = useState<string>('');
  const [internalId, setInternalId] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  useEffect(() => {
    if (open && initialTagName) {
      setShortName(initialTagName);
      setInternalId(cleanSpecialCharacters(initialTagName));
    }
  }, [open, initialTagName]);

  const enabled =
    internalId.length >= 2 && (model.tags.some((tag) => !parentId || tag.id === parentId) || model.tags.length === 0);

  function reset() {
    setInternalId('');
    setDescription('');
    setShortName('');
    setParentId(undefined);
  }

  function updateParent(tag: Tag | undefined) {
    setParentId(tag?.id || undefined);

    if (tag) {
      setInternalId(cleanSpecialCharacters(tag.shortName + '_' + shortName));
    } else {
      setInternalId(cleanSpecialCharacters(shortName));
    }
  }

  async function save() {
    if (model.tags.some((tag) => tag.internalId === internalId)) {
      errorToast('Tag already exists');
      return;
    }
    try {
      await newTag.mutateAsync({
        shortName,
        description,
        internalId,
        parentId,
      });
      if (createAnother) {
        reset();
      } else {
        closeAndReset();
      }
    } catch {
      errorToast('Failed to save tag');
    }
  }

  function closeAndReset() {
    reset();
    setCreateAnother(false);
    onOpenChange(false);
  }

  return (
    <BaseModal open={open} onOpenChange={onOpenChange}>
      <BaseModalContent
        subtleTitle={`${model.displayName} - New tag`}
        title={`New tag`}
        confirmText={'Create Tag'}
        submitLoading={newTag.isPending}
        confirmDisabled={!enabled}
        onCancel={closeAndReset}
        onConfirm={save}
        className="gap-2"
      >
        <div className="space-y-3 px-1 overflow-y-auto">
          <InputWithLabel
            autoFocus
            label="Tag Name"
            placeholder="Enter a name for your tag"
            value={shortName}
            onChange={(e) => {
              setShortName(e.target.value);
              setInternalId(cleanSpecialCharacters(e.target.value));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.shiftKey || e.metaKey)) {
                save();
              }
            }}
          />
          <TextArea
            label="Tag Description"
            placeholder="Descriptions can improve model accuracy by providing more context."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <div>
            <Popover open={parentSelectOpen} onOpenChange={setParentSelectOpen}>
              <Label>Parent tag (optional)</Label>
              <PopoverTrigger
                className={cn(
                  `border rounded-sm px-3 py-1.5 text-sm hover:border-typography-black data-[state=open]:border-typography-black 
                  flex items-center gap-2 transition-all h-9 hover:text-typography-black 
                data-[state=open]:text-typography-black w-full overflow-hidden data-[state=open]:rounded-b-none`,
                  !parentId && 'text-typography-grey'
                )}
              >
                <TagIcon size={14} className="shrink-0" />
                <div className="truncate">
                  {parentId ? model.tags.find((tag) => tag.id === parentId)?.shortName : 'No parent tag'}
                </div>
                <ChevronDown size={14} className="ml-auto shrink-0" />
              </PopoverTrigger>
              <PopoverContent sideOffset={-1} className="p-0 w-same-as-trigger border-black rounded-t-none">
                <Command className="p-0 w-full">
                  <CommandInput />
                  <PopoverClose className="p-0 w-full">
                    <CommandGroup className="w-full max-h-52 overflow-y-auto">
                      <CommandItem
                        onSelect={() => updateParent(undefined)}
                        className={cn(
                          'text-neutral-500 truncate max-w-full justify-between items-center flex',
                          !parentId && 'text-typography-black'
                        )}
                      >
                        <p>No parent tag</p>
                        <Check size={16} className={cn('shrink-0 ml-2 opacity-0', !parentId && 'opacity-100')} />
                      </CommandItem>
                      {model.tags
                        .sort((a, b) => a.hierarchy.join('').localeCompare(b.hierarchy.join('')))
                        .map((tag) => (
                          <CommandItem
                            key={tag.id}
                            value={tag.hierarchy.join('')}
                            onSelect={() => updateParent(tag)}
                            className={cn(
                              'text-neutral-500 truncate max-w-full justify-between items-center flex gap-4 w-full',
                              tag.id === parentId && 'text-typography-black'
                            )}
                          >
                            <TagHierarchy hierarchy={tag.hierarchy} />
                            <Check
                              size={16}
                              className={cn('shrink-0 opacity-0', tag.id === parentId && 'opacity-100')}
                            />
                          </CommandItem>
                        ))}
                    </CommandGroup>
                  </PopoverClose>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <WithAccessControl>
            <Accordion type="single" collapsible>
              <AccordionItem value="advanced">
                <AccordionTrigger
                  headless
                  className="group/trigger pt-3 pb-1 text-sm flex items-center gap-2 text-typography-grey hover:text-typography-black data-[state=open]:text-typography-black"
                >
                  <ChevronRight size={14} className="group-data-[state=open]/trigger:rotate-90 transition-transform" />
                  Advanced options
                </AccordionTrigger>
                <AccordionContent className="px-1">
                  <InputWithLabel
                    label="Internal ID"
                    placeholder="Must be lowercase and minimally 2 characters"
                    value={internalId}
                    onChange={(e) => setInternalId(e.target.value)}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </WithAccessControl>

          {!disableCreateMultiple && (
            <div className="flex items-center space-x-2 absolute bottom-8 group">
              <Checkbox
                id="createAnother"
                checked={createAnother}
                onCheckedChange={(checked) => setCreateAnother(checked as boolean)}
              />
              <Label htmlFor="createAnother" className="select-none group-hover:text-typography-black cursor-pointer">
                Create another tag
              </Label>
            </div>
          )}
        </div>
      </BaseModalContent>
    </BaseModal>
  );
}
